import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { FileText, Plus, Send, Download, Trash2, Loader2, Eye, Pencil, Save, Code, ChevronDown, ChevronUp, X, Upload, Clock, Database, Mail, DollarSign, Trophy, XCircle, Clock3, FileDown } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var EMPTY_ADDRESS = { supply_address: '', suburb: '', state: '', postcode: '', nmi: '', estimated_pk: '', estimated_opk: '', estimated_kwh: '', tariff: '', is_greenfield: false, is_life_support: false, is_dma: false };
var EMPTY_PRICING = { offer_type: 'stepped', step_y1_start: '', step_y1_peak: '', step_y1_offpeak: '', step_y1_shoulder: '', step_y2_start: '', step_y2_peak: '', step_y2_offpeak: '', step_y2_shoulder: '', step_y3_start: '', step_y3_peak: '', step_y3_offpeak: '', step_y3_shoulder: '', smooth_24_start: '', smooth_24_peak: '', smooth_24_offpeak: '', smooth_24_shoulder: '', smooth_36_start: '', smooth_36_peak: '', smooth_36_offpeak: '', smooth_36_shoulder: '' };
var EMPTY_ENV = { lgcs_cert: ['','','',''], stcs_cert: ['','','',''], state_scheme_cert: ['','','',''] };
var EMPTY_OTHER = { service_fee_monthly: '', dma_admin_fee_daily: '', metering_charge_monthly: '', load_flex_percent: '' };

function getInitialForm() {
  return {
    contract_state: 'QLD_SA', company_name: '', trading_name: '', customer_type: '', abn: '', acn: '',
    contact_name: '', phone: '', mobile: '', accounts_email: '', billing_address: '', billing_suburb: '',
    billing_state: '', billing_postcode: '', id_number: '', id_type: '', id_expiry: '', dob: '',
    connection_type: '', proposed_transfer_date: '', next_meter_read_date: '', contract_term_months: '',
    quote_valid_until: '', agreement_number: '', representative_name: '',
    supply_addresses: [{ ...EMPTY_ADDRESS }],
    pricing: { ...EMPTY_PRICING },
    env_charges: JSON.parse(JSON.stringify(EMPTY_ENV)),
    other_charges: { ...EMPTY_OTHER, service_fee_monthly: '80' },
    customer_sign_name: '', customer_sign_position: '', customer_sign_date: '',
    nbe_sign_name: '', nbe_sign_position: '', nbe_sign_date: '', status: 'draft',
    commission_type: '', commission_value: '',
  };
}

function SectionHeader({ title }) {
  return <h3 className="text-sm font-bold text-[#005394] uppercase tracking-wide border-b-2 border-[#29AAE0] pb-1 mb-3 mt-5">{title}</h3>;
}

function FormRow({ children, className }) {
  return <div className={'grid gap-3 mb-2 ' + (className || 'grid-cols-2')}>{children}</div>;
}

function Field({ label, value, onChange, placeholder, type, className }) {
  return (
    <div className={className || ''}>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input type={type || 'text'} value={value || ''} onChange={onChange} placeholder={placeholder || ''} className="h-8 text-sm" />
    </div>
  );
}

export default function ContractGenerator() {
  var authCtx = useAuth();
  var token = authCtx.token;
  var user = authCtx.user;
  var location = useLocation();
  var [tab, setTab] = useState('form');
  var [contracts, setContracts] = useState([]);
  var [form, setForm] = useState(getInitialForm());
  var [editingId, setEditingId] = useState(null);
  var [loading, setLoading] = useState(false);
  var [saving, setSaving] = useState(false);
  var [generating, setGenerating] = useState(false);
  var [emailDialog, setEmailDialog] = useState(false);
  var [emailTo, setEmailTo] = useState('');
  var [emailSubject, setEmailSubject] = useState('');
  var [emailMessage, setEmailMessage] = useState('');
  var [sendingEmail, setSendingEmail] = useState(false);
  var [emailContractId, setEmailContractId] = useState(null);
  var [templateHtml, setTemplateHtml] = useState('');
  var [templateEnabled, setTemplateEnabled] = useState(false);
  var [templateSaving, setTemplateSaving] = useState(false);
  var [expandedSections, setExpandedSections] = useState({ customer: true, connection: true, supply: true, pricing: true, env: true, other: true, signatures: true });

  // Rate Cards state
  var [rateCards, setRateCards] = useState([]);
  var [rateCardUploading, setRateCardUploading] = useState(false);
  var [selectedRateCard, setSelectedRateCard] = useState(null);
  var [rateCardDetail, setRateCardDetail] = useState(null);
  var [rateCardLoading, setRateCardLoading] = useState(false);
  var [confirmDelete, setConfirmDelete] = useState(null);
  var editFromNavRef = useRef(false);

  var fetchContracts = useCallback(function() {
    axios.get(API_URL + '/api/contracts?authorization=' + token).then(function(r) { setContracts(r.data); }).catch(function() {});
  }, [token]);

  useEffect(function() { fetchContracts(); }, [fetchContracts]);
  useEffect(function() { fetchRateCards(); }, [token]);

  // Auto-load contract from navigation state (e.g. from Email to C&I Generator)
  useEffect(function() {
    var editId = location.state && location.state.editContractId;
    if (editId && token && !editFromNavRef.current) {
      editFromNavRef.current = true;
      axios.get(API_URL + '/api/contracts/' + editId + '?authorization=' + token)
        .then(function(r) {
          loadContract(r.data);
          toast.success('Draft contract loaded');
        })
        .catch(function() {});
    }
  }, [location.state, token]);

  function updateForm(key, val) { setForm(function(f) { return { ...f, [key]: val }; }); }
  function updatePricing(key, val) { setForm(function(f) { return { ...f, pricing: { ...f.pricing, [key]: val } }; }); }
  function updateOther(key, val) { setForm(function(f) { return { ...f, other_charges: { ...f.other_charges, [key]: val } }; }); }
  function updateAddr(idx, key, val) {
    setForm(function(f) {
      var addrs = [...f.supply_addresses];
      addrs[idx] = { ...addrs[idx], [key]: val };
      return { ...f, supply_addresses: addrs };
    });
  }
  function updateEnvArr(key, idx, val) {
    setForm(function(f) {
      var env = { ...f.env_charges };
      var arr = [...(env[key] || ['','','',''])];
      arr[idx] = val;
      env[key] = arr;
      return { ...f, env_charges: env };
    });
  }
  function addAddress() { setForm(function(f) { return { ...f, supply_addresses: [...f.supply_addresses, { ...EMPTY_ADDRESS }] }; }); }
  function removeAddress(idx) { setForm(function(f) { var addrs = f.supply_addresses.filter(function(_, i) { return i !== idx; }); return { ...f, supply_addresses: addrs.length ? addrs : [{ ...EMPTY_ADDRESS }] }; }); }

  function toggleSection(key) { setExpandedSections(function(s) { return { ...s, [key]: !s[key] }; }); }

  function handleSave() {
    if (!form.company_name && !form.trading_name) { toast.error('Enter company or trading name'); return; }
    setSaving(true);
    var promise = editingId
      ? axios.put(API_URL + '/api/contracts/' + editingId + '?authorization=' + token, form)
      : axios.post(API_URL + '/api/contracts?authorization=' + token, form);
    promise.then(function(r) {
      toast.success(editingId ? 'Contract updated' : 'Contract saved');
      if (!editingId && r.data && r.data.id) setEditingId(r.data.id);
      fetchContracts();
    }).catch(function() { toast.error('Failed to save contract'); }).finally(function() { setSaving(false); });
  }

  function handleGeneratePdf() {
    var id = editingId;
    if (!id) { toast.error('Save the contract first'); return; }
    setGenerating(true);
    axios.get(API_URL + '/api/contracts/' + id + '/pdf?authorization=' + token, { responseType: 'blob' })
      .then(function(r) {
        var url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        var a = document.createElement('a');
        a.href = url;
        a.download = 'NBE_Contract_' + (form.agreement_number || id) + '.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      })
      .catch(function() { toast.error('Failed to generate PDF'); })
      .finally(function() { setGenerating(false); });
  }

  function handleSendEmail() {
    if (!emailTo) { toast.error('Enter customer email'); return; }
    setSendingEmail(true);
    var fd = new FormData();
    fd.append('customer_email', emailTo);
    fd.append('subject', emailSubject);
    fd.append('message', emailMessage);
    axios.post(API_URL + '/api/contracts/' + emailContractId + '/send-email?authorization=' + token, fd)
      .then(function() { toast.success('Contract sent to ' + emailTo); setEmailDialog(false); fetchContracts(); })
      .catch(function() { toast.error('Failed to send email'); })
      .finally(function() { setSendingEmail(false); });
  }

  function loadContract(c) {
    var pricing = c.pricing || { ...EMPTY_PRICING };
    // Normalize offer_type (AI extraction may return 'smooth' or 'flat')
    if (pricing.offer_type && pricing.offer_type !== 'stepped' && pricing.offer_type !== 'smoothed') {
      if (pricing.offer_type.startsWith('smooth')) pricing.offer_type = 'smoothed';
      else pricing.offer_type = 'stepped';
    }
    var otherCharges = c.other_charges || { ...EMPTY_OTHER };
    // Default service charge $80/month for C&I
    if (!otherCharges.service_fee_monthly) otherCharges.service_fee_monthly = '80';
    setForm({
      contract_state: c.contract_state || 'QLD_SA', company_name: c.company_name || '', trading_name: c.trading_name || '',
      customer_type: c.customer_type || '', abn: c.abn || '', acn: c.acn || '', contact_name: c.contact_name || '',
      phone: c.phone || '', mobile: c.mobile || '', accounts_email: c.accounts_email || '',
      billing_address: c.billing_address || '', billing_suburb: c.billing_suburb || '', billing_state: c.billing_state || '',
      billing_postcode: c.billing_postcode || '', id_number: c.id_number || '', id_type: c.id_type || '',
      id_expiry: c.id_expiry || '', dob: c.dob || '', connection_type: c.connection_type || '',
      proposed_transfer_date: c.proposed_transfer_date || '', next_meter_read_date: c.next_meter_read_date || '',
      contract_term_months: c.contract_term_months || '', quote_valid_until: c.quote_valid_until || '',
      agreement_number: c.agreement_number || '', representative_name: c.representative_name || '',
      supply_addresses: c.supply_addresses && c.supply_addresses.length > 0 ? c.supply_addresses : [{ ...EMPTY_ADDRESS }],
      pricing: pricing,
      env_charges: (c.env_charges && Object.keys(c.env_charges).length > 0) ? c.env_charges : JSON.parse(JSON.stringify(EMPTY_ENV)),
      other_charges: otherCharges,
      customer_sign_name: c.customer_sign_name || '', customer_sign_position: c.customer_sign_position || '',
      customer_sign_date: c.customer_sign_date || '', nbe_sign_name: c.nbe_sign_name || '',
      nbe_sign_position: c.nbe_sign_position || '', nbe_sign_date: c.nbe_sign_date || '',
      status: c.status || 'draft',
      commission_type: c.commission_type || '', commission_value: c.commission_value || '',
    });
    setEditingId(c.id);
    setTab('form');
  }

  function newContract() { setForm(getInitialForm()); setEditingId(null); setTab('form'); }

  function deleteContract(id) {
    setConfirmDelete({ type: 'contract', id: id });
  }
  function confirmDeleteAction() {
    if (!confirmDelete) return;
    var { type, id } = confirmDelete;
    setConfirmDelete(null);
    if (type === 'contract') {
      axios.delete(API_URL + '/api/contracts/' + id + '?authorization=' + token)
        .then(function() { toast.success('Contract deleted'); fetchContracts(); if (editingId === id) newContract(); })
        .catch(function() { toast.error('Failed to delete'); });
    } else if (type === 'ratecard') {
      axios.delete(API_URL + '/api/ci-rate-cards/' + id + '?authorization=' + token)
        .then(function() { toast.success('Rate card deleted'); fetchRateCards(); if (selectedRateCard === id) { setSelectedRateCard(null); setRateCardDetail(null); } })
        .catch(function() { toast.error('Failed to delete'); });
    }
  }

  function updateOutcome(id, outcome) {
    var fd = new FormData();
    fd.append('outcome', outcome);
    axios.put(API_URL + '/api/contracts/' + id + '/outcome?authorization=' + token, fd)
      .then(function() { toast.success('Outcome updated to ' + outcome); fetchContracts(); })
      .catch(function() { toast.error('Failed to update outcome'); });
  }

  function downloadReport() {
    var a = document.createElement('a'); a.href = API_URL + '/api/contracts/export-csv?authorization=' + token; a.download = 'contracts_export.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  function loadTemplate() {
    axios.get(API_URL + '/api/settings/contract-template?authorization=' + token)
      .then(function(r) {
        setTemplateHtml(r.data.html_template || '');
        setTemplateEnabled(r.data.enabled || false);
        // If no custom template saved yet, load the default
        if (!r.data.html_template) {
          loadDefaultTemplate();
        }
      })
      .catch(function() {});
  }

  function saveTemplate() {
    setTemplateSaving(true);
    var fd = new FormData();
    fd.append('html_template', templateHtml);
    fd.append('enabled', templateEnabled ? 'true' : 'false');
    axios.post(API_URL + '/api/settings/contract-template?authorization=' + token, fd)
      .then(function() { toast.success('Template saved'); })
      .catch(function() { toast.error('Failed to save template'); })
      .finally(function() { setTemplateSaving(false); });
  }

  var stateSchemeLabel = form.contract_state === 'VIC' ? 'VEEC' : form.contract_state === 'NSW' ? 'ESC' : '';
  var envScheme1Label = 'LREC';
  var envScheme2Label = 'SREC';

  var [previewLoading, setPreviewLoading] = useState(false);

  function handlePreviewPdf() {
    setPreviewLoading(true);
    axios.post(API_URL + '/api/contracts/preview-pdf?authorization=' + token, form, { responseType: 'blob' })
      .then(function(r) {
        var url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        var a = document.createElement('a'); a.href = url; a.download = 'Contract_Preview.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Preview PDF downloaded');
      })
      .catch(function() { toast.error('Failed to generate preview'); })
      .finally(function() { setPreviewLoading(false); });
  }

  function handlePreviewTemplate() {
    if (!templateHtml) { toast.error('No template HTML to preview'); return; }
    setPreviewLoading(true);
    var fd = new FormData();
    fd.append('html_template', templateHtml);
    axios.post(API_URL + '/api/contracts/preview-custom-template?authorization=' + token, fd, { responseType: 'blob' })
      .then(function(r) {
        var url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        var a = document.createElement('a'); a.href = url; a.download = 'Template_Preview.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
        toast.success('Template preview PDF downloaded');
      })
      .catch(function() { toast.error('Failed to generate template preview'); })
      .finally(function() { setPreviewLoading(false); });
  }

  function loadDefaultTemplate() {
    axios.get(API_URL + '/api/contracts/default-template-html?authorization=' + token)
      .then(function(r) { if (r.data && r.data.html) { setTemplateHtml(r.data.html); toast.success('Default template loaded'); } })
      .catch(function() { toast.error('Failed to load default template'); });
  }

  // Rate Card functions
  function fetchRateCards() {
    axios.get(API_URL + '/api/ci-rate-cards?authorization=' + token)
      .then(function(r) { setRateCards(r.data); })
      .catch(function() {});
  }

  function handleRateCardUpload(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are accepted');
      return;
    }
    setRateCardUploading(true);
    var fd = new FormData();
    fd.append('file', file);
    axios.post(API_URL + '/api/ci-rate-cards/upload?authorization=' + token, fd)
      .then(function(r) {
        toast.success('Rate card uploaded and parsed successfully');
        fetchRateCards();
        setSelectedRateCard(r.data.id);
        setRateCardDetail(r.data);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        toast.error(msg || 'Failed to upload rate card');
      })
      .finally(function() { setRateCardUploading(false); e.target.value = ''; });
  }

  function viewRateCard(id) {
    setRateCardLoading(true);
    setSelectedRateCard(id);
    axios.get(API_URL + '/api/ci-rate-cards/' + id + '?authorization=' + token)
      .then(function(r) { setRateCardDetail(r.data); })
      .catch(function() { toast.error('Failed to load rate card'); })
      .finally(function() { setRateCardLoading(false); });
  }

  function deleteRateCard(id) {
    setConfirmDelete({ type: 'ratecard', id: id });
  }

  function applyRateCardToContract(quarter) {
    if (!rateCardDetail || !rateCardDetail.data) { toast.error('No rate card data loaded'); return; }
    var d = rateCardDetail.data;
    var q = d.quarters && d.quarters[quarter || 'Q1'];
    if (!q) { toast.error('No data for selected quarter'); return; }

    // Map contract_state to rate card state key
    var stateMap = { 'QLD_SA': null, 'NSW': 'NSW', 'VIC': 'VIC' };
    var stateKey = stateMap[form.contract_state];
    // QLD_SA needs special handling - try QLD first, then SA
    if (form.contract_state === 'QLD_SA') {
      stateKey = q.energy_rates['QLD'] ? 'QLD' : (q.energy_rates['SA'] ? 'SA' : null);
    }
    if (!stateKey || !q.energy_rates[stateKey]) { toast.error('No rates found for state: ' + form.contract_state); return; }

    var rates = q.energy_rates[stateKey];
    var env = d.environmental || {};
    var met = d.metering || {};

    setForm(function(f) {
      var newPricing = { ...f.pricing };
      // Apply stepped rates
      var stepped = rates.stepped || {};
      if (stepped['1']) {
        newPricing.step_y1_peak = stepped['1'].peak != null ? String(stepped['1'].peak) : '';
        newPricing.step_y1_shoulder = stepped['1'].shoulder != null ? String(stepped['1'].shoulder) : '';
        newPricing.step_y1_offpeak = stepped['1'].offpeak != null ? String(stepped['1'].offpeak) : '';
      }
      if (stepped['2']) {
        newPricing.step_y2_peak = stepped['2'].peak != null ? String(stepped['2'].peak) : '';
        newPricing.step_y2_shoulder = stepped['2'].shoulder != null ? String(stepped['2'].shoulder) : '';
        newPricing.step_y2_offpeak = stepped['2'].offpeak != null ? String(stepped['2'].offpeak) : '';
      }
      if (stepped['3']) {
        newPricing.step_y3_peak = stepped['3'].peak != null ? String(stepped['3'].peak) : '';
        newPricing.step_y3_shoulder = stepped['3'].shoulder != null ? String(stepped['3'].shoulder) : '';
        newPricing.step_y3_offpeak = stepped['3'].offpeak != null ? String(stepped['3'].offpeak) : '';
      }
      // Apply smoothed rates
      var smoothed = rates.smoothed || {};
      if (smoothed['2']) {
        newPricing.smooth_24_peak = smoothed['2'].peak != null ? String(smoothed['2'].peak) : '';
        newPricing.smooth_24_shoulder = smoothed['2'].shoulder != null ? String(smoothed['2'].shoulder) : '';
        newPricing.smooth_24_offpeak = smoothed['2'].offpeak != null ? String(smoothed['2'].offpeak) : '';
      }
      if (smoothed['3']) {
        newPricing.smooth_36_peak = smoothed['3'].peak != null ? String(smoothed['3'].peak) : '';
        newPricing.smooth_36_shoulder = smoothed['3'].shoulder != null ? String(smoothed['3'].shoulder) : '';
        newPricing.smooth_36_offpeak = smoothed['3'].offpeak != null ? String(smoothed['3'].offpeak) : '';
      }

      // Environmental charges - map LREC->lgcs, SREC->stcs, state schemes
      var newEnv = JSON.parse(JSON.stringify(f.env_charges));
      var lrec = env['LREC'] || {};
      var srec = env['SREC'] || {};
      var veec = env['VEEC'] || {};
      var esc = env['ESC'] || {};
      // LGCS/LRET = LREC
      newEnv.lgcs_cert = [lrec.CAL26 != null ? String(lrec.CAL26) : '', lrec.CAL27 != null ? String(lrec.CAL27) : '', lrec.CAL28 != null ? String(lrec.CAL28) : '', ''];
      // STCS/SRES = SREC
      newEnv.stcs_cert = [srec.CAL26 != null ? String(srec.CAL26) : '', srec.CAL27 != null ? String(srec.CAL27) : '', srec.CAL28 != null ? String(srec.CAL28) : '', ''];
      // State scheme depends on state
      if (form.contract_state === 'VIC') {
        newEnv.state_scheme_cert = [veec.CAL26 != null ? String(veec.CAL26) : '', veec.CAL27 != null ? String(veec.CAL27) : '', veec.CAL28 != null ? String(veec.CAL28) : '', ''];
      } else if (form.contract_state === 'NSW') {
        newEnv.state_scheme_cert = [esc.CAL26 != null ? String(esc.CAL26) : '', esc.CAL27 != null ? String(esc.CAL27) : '', esc.CAL28 != null ? String(esc.CAL28) : '', ''];
      }

      // Service charge & metering
      var newOther = { ...f.other_charges };
      var anyRate = stepped['1'] || smoothed['2'] || {};
      if (anyRate.service_charge != null) {
        newOther.service_fee_monthly = String(anyRate.service_charge);
      }
      // Metering: use COMMS4 as default for C&I
      var meterVal = met['COMMS4'] || met['COMMS3'];
      if (meterVal != null && typeof meterVal === 'number') {
        newOther.metering_charge_monthly = String(meterVal);
      }

      return { ...f, pricing: newPricing, env_charges: newEnv, other_charges: newOther };
    });

    toast.success('Rates applied from rate card (' + quarter + ', ' + stateKey + ')');
    setTab('form');
  }

  function applyCommissionToRates() {
    var commVal = parseFloat(form.commission_value);
    if (!commVal || commVal <= 0) { toast.error('Enter a valid commission value'); return; }
    var commType = form.commission_type || 'percent';
    setForm(function(f) {
      var p = { ...f.pricing };
      var rateKeys = Object.keys(p).filter(function(k) { return k.endsWith('_peak') || k.endsWith('_shoulder') || k.endsWith('_offpeak'); });
      rateKeys.forEach(function(k) {
        var base = parseFloat(p[k]);
        if (!isNaN(base) && base > 0) {
          if (commType === 'percent') {
            p[k] = String((base * (1 + commVal / 100)).toFixed(4));
          } else {
            p[k] = String((base + commVal).toFixed(4));
          }
        }
      });
      return { ...f, pricing: p };
    });
    toast.success('Commission applied to pricing rates');
  }

  async function handleMailto(contractId, companyName, email, contractObj) {
    // Download PDF first (same pattern as working Comparison Tool)
    try {
      var res = await axios.get(API_URL + '/api/contracts/' + contractId + '/pdf?authorization=' + token, { responseType: 'blob' });
      var blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      var link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'NBE_Contract_' + (companyName || contractId).replace(/\s+/g, '_') + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('PDF download failed:', err);
    }

    // Build email with contract details
    var c = contractObj || form;
    var subject = 'Next Business Energy - C&I Contract Agreement' + (companyName ? ' - ' + companyName : '');
    var bodyLines = [
      'Hi,',
      '',
      'Please find attached the C&I Energy Contract Agreement from Next Business Energy.',
      '',
      '--- Contract Details ---',
      'Company: ' + (companyName || 'N/A'),
      'Agreement: ' + (c.agreement_number || contractId),
      'Contact: ' + (c.contact_name || 'N/A'),
      'State: ' + (c.contract_state || 'N/A'),
      'Start Date: ' + (c.proposed_transfer_date || 'N/A'),
    ];
    if (c.commission_type && c.commission_value) {
      bodyLines.push('Commission: ' + c.commission_value + (c.commission_type === 'percent' ? '%' : ' c/kWh'));
    }
    bodyLines.push('', 'Please review the agreement and sign where indicated.', '', 'Please attach the downloaded PDF to this email.', '', 'Kind regards,', (c.representative_name || 'Next Business Energy'));

    var mailto = 'mailto:' + (email || '') + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(bodyLines.join('\n'));
    window.location.href = mailto;
    toast.success('PDF downloaded. Please attach it to the email.');
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 lg:p-6" data-testid="contract-generator">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">C&I Contract Generator</h1>
            <p className="text-slate-500 text-sm">Generate Commercial & Industrial customer agreements</p>
          </div>
          <Button onClick={newContract} className="rounded-full bg-[#005394] hover:bg-[#005394]/90" data-testid="new-contract-btn">
            <Plus className="w-4 h-4 mr-2" />New Contract
          </Button>
        </div>

        <Tabs value={tab} onValueChange={function(v) { if (v === 'template') loadTemplate(); if (v === 'ratecards') fetchRateCards(); setTab(v); }}>
          <TabsList className="mb-4">
            <TabsTrigger value="form" data-testid="tab-form">Contract Form</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">Saved Contracts</TabsTrigger>
            <TabsTrigger value="ratecards" data-testid="tab-ratecards">Rate Cards</TabsTrigger>
            {user && user.role === 'admin' && <TabsTrigger value="template" data-testid="tab-template">HTML Template</TabsTrigger>}
          </TabsList>

          {/* ====== CONTRACT FORM ====== */}
          <TabsContent value="form">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{editingId ? 'Edit Contract' : 'New C&I Contract'}</CardTitle>
                    <CardDescription>
                      {editingId && <Badge className="bg-blue-100 text-blue-800 mr-2">{form.agreement_number || editingId.slice(0,8)}</Badge>}
                      {form.status === 'sent' ? <Badge className="bg-green-100 text-green-800">Sent</Badge> : <Badge variant="outline">Draft</Badge>}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-contract-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save
                    </Button>
                    <Button onClick={handleGeneratePdf} disabled={!editingId || generating} className="bg-[#005394] hover:bg-[#005394]/90" data-testid="generate-pdf-btn">
                      {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}Generate PDF
                    </Button>
                    <Button variant="outline" onClick={handlePreviewPdf} disabled={previewLoading} className="border-[#00A99D] text-[#00A99D] hover:bg-[#00A99D]/10" data-testid="preview-pdf-btn">
                      {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}Preview PDF
                    </Button>
                    {editingId && (
                      <Button variant="outline" onClick={function() { setEmailContractId(editingId); setEmailTo(form.accounts_email || ''); setEmailDialog(true); }} data-testid="send-email-btn">
                        <Send className="w-4 h-4 mr-1" />Send to Customer
                      </Button>
                    )}
                    {editingId && (
                      <Button variant="outline" onClick={function() { handleMailto(editingId, form.company_name, form.accounts_email); }} className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="mailto-btn">
                        <Mail className="w-4 h-4 mr-1" />Open in Mail
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>

                {/* State selector */}
                <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Contract State Version</Label>
                      <Select value={form.contract_state} onValueChange={function(v) { updateForm('contract_state', v); }}>
                        <SelectTrigger className="h-8" data-testid="state-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="QLD_SA">QLD / SA</SelectItem>
                          <SelectItem value="NSW">NSW</SelectItem>
                          <SelectItem value="VIC">VIC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label="Agreement Number" value={form.agreement_number} onChange={function(e) { updateForm('agreement_number', e.target.value); }} placeholder="Auto-generated if empty" />
                    <Field label="Sales Representative" value={form.representative_name} onChange={function(e) { updateForm('representative_name', e.target.value); }} />
                  </div>
                </div>

                {/* Rate Card Selector */}
                {rateCards.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="rate-card-selector">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs font-semibold text-amber-800">Load Rates from Rate Card</Label>
                        <Select value={selectedRateCard || ''} onValueChange={function(v) { viewRateCard(v); }}>
                          <SelectTrigger className="h-8 bg-white" data-testid="rate-card-select"><SelectValue placeholder="Select a rate card..." /></SelectTrigger>
                          <SelectContent>
                            {rateCards.map(function(rc) {
                              return <SelectItem key={rc.id} value={rc.id}>{rc.filename} ({new Date(rc.uploaded_at).toLocaleDateString()})</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button size="sm" onClick={function() { applyRateCardToContract('Q1'); }} disabled={!rateCardDetail} className="bg-[#005394] hover:bg-[#005394]/90" data-testid="apply-rates-q1">
                          <Database className="w-3.5 h-3.5 mr-1" />Apply Q1
                        </Button>
                        <Button size="sm" variant="outline" onClick={function() { applyRateCardToContract('Q2'); }} disabled={!rateCardDetail} className="border-[#005394] text-[#005394] hover:bg-[#005394]/10" data-testid="apply-rates-q2">
                          Apply Q2
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* CUSTOMER DETAILS */}
                <CollapsibleSection title="Customer Details" sKey="customer" expanded={expandedSections.customer} toggle={toggleSection}>
                  <FormRow className="grid-cols-2">
                    <Field label="Company Name *" value={form.company_name} onChange={function(e) { updateForm('company_name', e.target.value); }} data-testid="company-name" />
                    <Field label="Trading Name" value={form.trading_name} onChange={function(e) { updateForm('trading_name', e.target.value); }} />
                  </FormRow>
                  <div className="mb-2">
                    <Label className="text-xs text-slate-600">Customer Type</Label>
                    <div className="flex gap-3 mt-1">
                      {[['sole_trader','Sole Trader'],['partnership','Partnership'],['company','Company'],['business','Business'],['trust','Trust']].map(function(ct) {
                        return <label key={ct[0]} className={'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md cursor-pointer border transition-colors ' + (form.customer_type === ct[0] ? 'bg-[#005394]/10 border-[#005394] text-[#005394] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}>
                          <input type="radio" name="customer_type" value={ct[0]} checked={form.customer_type === ct[0]} onChange={function() { updateForm('customer_type', ct[0]); }} className="sr-only" />
                          {ct[1]}
                        </label>;
                      })}
                    </div>
                  </div>
                  <FormRow><Field label="ABN" value={form.abn} onChange={function(e) { updateForm('abn', e.target.value); }} /><Field label="ACN" value={form.acn} onChange={function(e) { updateForm('acn', e.target.value); }} /></FormRow>
                  <Field label="Contact Name" value={form.contact_name} onChange={function(e) { updateForm('contact_name', e.target.value); }} className="mb-2" />
                  <FormRow><Field label="Phone" value={form.phone} onChange={function(e) { updateForm('phone', e.target.value); }} /><Field label="Mobile" value={form.mobile} onChange={function(e) { updateForm('mobile', e.target.value); }} /></FormRow>
                  <Field label="Accounts Email" value={form.accounts_email} onChange={function(e) { updateForm('accounts_email', e.target.value); }} className="mb-2" />
                  <Field label="Billing Address" value={form.billing_address} onChange={function(e) { updateForm('billing_address', e.target.value); }} className="mb-2" />
                  <FormRow className="grid-cols-3">
                    <Field label="Suburb" value={form.billing_suburb} onChange={function(e) { updateForm('billing_suburb', e.target.value); }} />
                    <Field label="State" value={form.billing_state} onChange={function(e) { updateForm('billing_state', e.target.value); }} />
                    <Field label="Postcode" value={form.billing_postcode} onChange={function(e) { updateForm('billing_postcode', e.target.value); }} />
                  </FormRow>
                  <p className="text-xs text-slate-400 italic mb-1 mt-2">ID section — not required for Company Agreements</p>
                  <FormRow className="grid-cols-4">
                    <Field label="ID No." value={form.id_number} onChange={function(e) { updateForm('id_number', e.target.value); }} />
                    <Field label="ID Type" value={form.id_type} onChange={function(e) { updateForm('id_type', e.target.value); }} />
                    <Field label="Expiry" value={form.id_expiry} onChange={function(e) { updateForm('id_expiry', e.target.value); }} type="date" />
                    <Field label="D.O.B" value={form.dob} onChange={function(e) { updateForm('dob', e.target.value); }} type="date" />
                  </FormRow>
                </CollapsibleSection>

                {/* CONNECTION TYPE & DATES */}
                <CollapsibleSection title="Connection & Important Dates" sKey="connection" expanded={expandedSections.connection} toggle={toggleSection}>
                  <div className="mb-3">
                    <Label className="text-xs text-slate-600">Connection Type</Label>
                    <div className="flex gap-3 mt-1">
                      {[['transfer','Transfer'],['move_in','Move In'],['renewal','Renewal']].map(function(ct) {
                        return <label key={ct[0]} className={'flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md cursor-pointer border transition-colors ' + (form.connection_type === ct[0] ? 'bg-[#005394]/10 border-[#005394] text-[#005394] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}>
                          <input type="radio" name="connection_type" value={ct[0]} checked={form.connection_type === ct[0]} onChange={function() { updateForm('connection_type', ct[0]); }} className="sr-only" />
                          {ct[1]}
                        </label>;
                      })}
                    </div>
                  </div>
                  <FormRow className="grid-cols-2">
                    <Field label="Proposed Transfer Date" value={form.proposed_transfer_date} onChange={function(e) { updateForm('proposed_transfer_date', e.target.value); }} type="date" />
                    <Field label="Next Scheduled Meter Read" value={form.next_meter_read_date} onChange={function(e) { updateForm('next_meter_read_date', e.target.value); }} type="date" />
                  </FormRow>
                  <FormRow className="grid-cols-2">
                    <Field label="Contract Term (Months)" value={form.contract_term_months} onChange={function(e) { updateForm('contract_term_months', e.target.value); }} placeholder="36" />
                    <Field label="Quote Valid Until" value={form.quote_valid_until} onChange={function(e) { updateForm('quote_valid_until', e.target.value); }} type="date" />
                  </FormRow>
                </CollapsibleSection>

                {/* SUPPLY ADDRESSES */}
                <CollapsibleSection title={'Supply Address Details (' + form.supply_addresses.length + ')'} sKey="supply" expanded={expandedSections.supply} toggle={toggleSection}>
                  {form.supply_addresses.map(function(addr, idx) {
                    return (
                      <div key={idx} className="border rounded-lg p-3 mb-3 bg-white relative">
                        {form.supply_addresses.length > 1 && (
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-[#005394]">Site {idx + 1}</span>
                            <Button variant="ghost" size="sm" onClick={function() { removeAddress(idx); }} className="h-6 text-red-500 hover:text-red-700"><X className="w-3 h-3" /></Button>
                          </div>
                        )}
                        <Field label="Supply Address" value={addr.supply_address} onChange={function(e) { updateAddr(idx, 'supply_address', e.target.value); }} className="mb-2" />
                        <FormRow className="grid-cols-4">
                          <Field label="Suburb" value={addr.suburb} onChange={function(e) { updateAddr(idx, 'suburb', e.target.value); }} />
                          <Field label="State" value={addr.state} onChange={function(e) { updateAddr(idx, 'state', e.target.value); }} />
                          <Field label="Postcode" value={addr.postcode} onChange={function(e) { updateAddr(idx, 'postcode', e.target.value); }} />
                          <Field label="NMI" value={addr.nmi} onChange={function(e) { updateAddr(idx, 'nmi', e.target.value); }} />
                        </FormRow>
                        <FormRow className="grid-cols-4">
                          <Field label="Est. Peak kWh" value={addr.estimated_pk} onChange={function(e) { updateAddr(idx, 'estimated_pk', e.target.value); }} />
                          <Field label="Est. Off-Peak kWh" value={addr.estimated_opk} onChange={function(e) { updateAddr(idx, 'estimated_opk', e.target.value); }} />
                          <Field label="Total kWh" value={addr.estimated_kwh} onChange={function(e) { updateAddr(idx, 'estimated_kwh', e.target.value); }} />
                          <Field label="Tariff" value={addr.tariff} onChange={function(e) { updateAddr(idx, 'tariff', e.target.value); }} />
                        </FormRow>
                        <div className="flex gap-4 mt-2">
                          {[['is_greenfield','Greenfield Site'],['is_life_support','Life Support'],['is_dma','DMA']].map(function(f) {
                            return <label key={f[0]} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                              <input type="checkbox" checked={!!addr[f[0]]} onChange={function(e) { updateAddr(idx, f[0], e.target.checked); }} className="rounded border-slate-300" />{f[1]}
                            </label>;
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <Button variant="outline" size="sm" onClick={addAddress} className="w-full border-dashed" data-testid="add-address-btn"><Plus className="w-4 h-4 mr-1" />Add Supply Address</Button>
                </CollapsibleSection>

                {/* PRICING SCHEDULE */}
                <CollapsibleSection title="Pricing Schedule (Ex GST)" sKey="pricing" expanded={expandedSections.pricing} toggle={toggleSection}>
                  <div className="mb-3">
                    <Label className="text-xs text-slate-600">Offer Type</Label>
                    <div className="flex gap-3 mt-1">
                      {[['stepped','Stepped'],['smoothed','Smoothed']].map(function(ot) {
                        return <label key={ot[0]} className={'flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md cursor-pointer border transition-colors ' + (form.pricing.offer_type === ot[0] ? 'bg-[#005394]/10 border-[#005394] text-[#005394] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}>
                          <input type="radio" name="offer_type" value={ot[0]} checked={form.pricing.offer_type === ot[0]} onChange={function() { updatePricing('offer_type', ot[0]); }} className="sr-only" />
                          {ot[1]}
                        </label>;
                      })}
                    </div>
                  </div>

                  {/* Commission */}
                  <div className="mb-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="commission-section">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      <Label className="text-xs font-semibold text-emerald-800 flex-shrink-0">Broker Commission</Label>
                      <div className="flex gap-2 items-center">
                        {[['percent', '% Markup'], ['ckwh', 'c/kWh']].map(function(ct) {
                          return <label key={ct[0]} className={'flex items-center gap-1 text-xs px-2 py-1 rounded cursor-pointer border transition-colors ' + (form.commission_type === ct[0] ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50')}>
                            <input type="radio" name="comm_type" value={ct[0]} checked={form.commission_type === ct[0]} onChange={function() { updateForm('commission_type', ct[0]); }} className="sr-only" />
                            {ct[1]}
                          </label>;
                        })}
                      </div>
                      <Input className="h-7 text-xs w-24" value={form.commission_value} onChange={function(e) { updateForm('commission_value', e.target.value); }} placeholder="e.g. 5" data-testid="commission-value-input" />
                      <Button size="sm" variant="outline" onClick={applyCommissionToRates} disabled={!form.commission_type || !form.commission_value} className="h-7 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-100" data-testid="apply-commission-btn">
                        Apply to Rates
                      </Button>
                    </div>
                    {form.commission_type && form.commission_value && (
                      <p className="text-xs text-emerald-600 mt-1 ml-7">Saved: {form.commission_value}{form.commission_type === 'percent' ? '%' : ' c/kWh'} commission</p>
                    )}
                  </div>

                  {form.pricing.offer_type === 'stepped' ? (
                    <div className="space-y-2">
                      {[['y1','Year 1 (12 Months)'],['y2','Year 2 (24 Months)'],['y3','Year 3 (36 Months)']].map(function(yr) {
                        return (
                          <div key={yr[0]} className="border rounded-lg p-2 bg-slate-50">
                            <p className="text-xs font-semibold text-slate-700 mb-1">{yr[1]}</p>
                            <FormRow className="grid-cols-4">
                              <Field label="Start Date" value={form.pricing['step_' + yr[0] + '_start']} onChange={function(e) { updatePricing('step_' + yr[0] + '_start', e.target.value); }} type="date" />
                              <Field label="Peak c/kWh" value={form.pricing['step_' + yr[0] + '_peak']} onChange={function(e) { updatePricing('step_' + yr[0] + '_peak', e.target.value); }} />
                              <Field label="Off Peak c/kWh" value={form.pricing['step_' + yr[0] + '_offpeak']} onChange={function(e) { updatePricing('step_' + yr[0] + '_offpeak', e.target.value); }} />
                              <Field label="Shoulder c/kWh" value={form.pricing['step_' + yr[0] + '_shoulder']} onChange={function(e) { updatePricing('step_' + yr[0] + '_shoulder', e.target.value); }} />
                            </FormRow>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[['24','24 Months (Years 1 & 2)'],['36','36 Months (Years 1, 2 & 3)']].map(function(sm) {
                        return (
                          <div key={sm[0]} className="border rounded-lg p-2 bg-slate-50">
                            <p className="text-xs font-semibold text-slate-700 mb-1">{sm[1]}</p>
                            <FormRow className="grid-cols-4">
                              <Field label="Start Date" value={form.pricing['smooth_' + sm[0] + '_start']} onChange={function(e) { updatePricing('smooth_' + sm[0] + '_start', e.target.value); }} type="date" />
                              <Field label="Peak c/kWh" value={form.pricing['smooth_' + sm[0] + '_peak']} onChange={function(e) { updatePricing('smooth_' + sm[0] + '_peak', e.target.value); }} />
                              <Field label="Off Peak c/kWh" value={form.pricing['smooth_' + sm[0] + '_offpeak']} onChange={function(e) { updatePricing('smooth_' + sm[0] + '_offpeak', e.target.value); }} />
                              <Field label="Shoulder c/kWh" value={form.pricing['smooth_' + sm[0] + '_shoulder']} onChange={function(e) { updatePricing('smooth_' + sm[0] + '_shoulder', e.target.value); }} />
                            </FormRow>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-slate-400 italic mt-1">Energy rates exclude loss factors.</p>
                </CollapsibleSection>

                {/* ENVIRONMENTAL CHARGES */}
                <CollapsibleSection title="Environmental Charges (Ex GST)" sKey="env" expanded={expandedSections.env} toggle={toggleSection}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-[#005394] text-white">
                          <th className="p-2 text-left font-medium rounded-tl-md">Scheme</th>
                          <th className="p-2 text-center font-medium">CAL 26</th>
                          <th className="p-2 text-center font-medium">CAL 27</th>
                          <th className="p-2 text-center font-medium">CAL 28</th>
                          <th className="p-2 text-center font-medium rounded-tr-md">CAL 29</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-slate-100"><td colSpan={5} className="p-1.5 font-semibold text-slate-700">Certificate Price $/MWh (Ex GST)</td></tr>
                        {[['lgcs_cert', envScheme1Label],['stcs_cert', envScheme2Label]].concat(stateSchemeLabel ? [['state_scheme_cert', stateSchemeLabel]] : []).map(function(row) {
                          return (
                            <tr key={row[0]} className="border-b">
                              <td className="p-1.5 font-medium">{row[1]}</td>
                              {[0,1,2,3].map(function(i) {
                                return <td key={i} className="p-1"><Input className="h-7 text-xs text-center" value={(form.env_charges[row[0]] || [])[i] || ''} onChange={function(e) { updateEnvArr(row[0], i, e.target.value); }} /></td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {form.contract_state === 'VIC' && <p className="text-xs text-amber-600 mt-1">VEEC ends in 2026, hence afterwards it will be a cost pass through if this scheme is extended.</p>}
                  {form.contract_state === 'NSW' && <p className="text-xs text-amber-600 mt-1">ESC scheme may end in 2026; afterwards it will be a cost pass-through if extended.</p>}
                </CollapsibleSection>

                {/* OTHER CHARGES */}
                <CollapsibleSection title="Other Charges (Ex GST)" sKey="other" expanded={expandedSections.other} toggle={toggleSection}>
                  <FormRow className="grid-cols-2">
                    <Field label="Service Fee - $/month" value={form.other_charges.service_fee_monthly} onChange={function(e) { updateOther('service_fee_monthly', e.target.value); }} />
                    <Field label="DMA Admin Fee - c/day (if applicable)" value={form.other_charges.dma_admin_fee_daily} onChange={function(e) { updateOther('dma_admin_fee_daily', e.target.value); }} />
                  </FormRow>
                  <FormRow className="grid-cols-2">
                    <Field label="Metering Charge - $/month (if applicable)" value={form.other_charges.metering_charge_monthly} onChange={function(e) { updateOther('metering_charge_monthly', e.target.value); }} />
                    <Field label="Load Flex +/- (%)" value={form.other_charges.load_flex_percent} onChange={function(e) { updateOther('load_flex_percent', e.target.value); }} />
                  </FormRow>
                </CollapsibleSection>

                {/* SIGNATURES */}
                <CollapsibleSection title="Signatures" sKey="signatures" expanded={expandedSections.signatures} toggle={toggleSection}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#005394] mb-2">Customer Signature</p>
                      <div className="h-16 border-2 border-dashed border-slate-300 rounded-lg mb-2 flex items-center justify-center text-xs text-slate-400">Signature area (on printed PDF)</div>
                      <Field label="Print Name" value={form.customer_sign_name} onChange={function(e) { updateForm('customer_sign_name', e.target.value); }} className="mb-1" />
                      <Field label="Position/Role" value={form.customer_sign_position} onChange={function(e) { updateForm('customer_sign_position', e.target.value); }} className="mb-1" />
                      <Field label="Date" value={form.customer_sign_date} onChange={function(e) { updateForm('customer_sign_date', e.target.value); }} type="date" />
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#005394] mb-2">NBE Signature <span className="font-normal text-slate-400">(Office use)</span></p>
                      <div className="h-16 border-2 border-dashed border-slate-300 rounded-lg mb-2 flex items-center justify-center text-xs text-slate-400">Signature area (on printed PDF)</div>
                      <Field label="Print Name" value={form.nbe_sign_name} onChange={function(e) { updateForm('nbe_sign_name', e.target.value); }} className="mb-1" />
                      <Field label="Position/Role" value={form.nbe_sign_position} onChange={function(e) { updateForm('nbe_sign_position', e.target.value); }} className="mb-1" />
                      <Field label="Date" value={form.nbe_sign_date} onChange={function(e) { updateForm('nbe_sign_date', e.target.value); }} type="date" />
                    </div>
                  </div>
                </CollapsibleSection>

              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== SAVED CONTRACTS LIST ====== */}
          <TabsContent value="list">
            <div className="space-y-3">
              <div className="flex justify-end mb-2">
                <Button variant="outline" size="sm" onClick={downloadReport} data-testid="download-report-btn">
                  <FileDown className="w-4 h-4 mr-1.5" />Download Report (CSV)
                </Button>
              </div>
              {contracts.length === 0 && <Card><CardContent className="py-12 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No contracts yet</p></CardContent></Card>}
              {contracts.map(function(c) {
                var outcomeBadge = null;
                if (c.outcome === 'won') outcomeBadge = <Badge className="bg-green-100 text-green-800" data-testid={'outcome-badge-' + c.id}>Won</Badge>;
                else if (c.outcome === 'lost') outcomeBadge = <Badge className="bg-red-100 text-red-800" data-testid={'outcome-badge-' + c.id}>Lost</Badge>;
                else if (c.outcome === 'pending') outcomeBadge = <Badge className="bg-yellow-100 text-yellow-800" data-testid={'outcome-badge-' + c.id}>Pending</Badge>;
                return (
                  <Card key={c.id} className={'border-l-4 hover:shadow-md transition-shadow ' + (c.outcome === 'won' ? 'border-l-green-500' : c.outcome === 'lost' ? 'border-l-red-400' : c.outcome === 'pending' ? 'border-l-yellow-400' : 'border-l-[#005394]')} data-testid={'contract-' + c.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{c.company_name || c.trading_name || 'Untitled'}</h3>
                            <Badge variant="outline" className="text-xs">{c.agreement_number || c.id.slice(0,8)}</Badge>
                            <Badge className={c.contract_state === 'VIC' ? 'bg-purple-100 text-purple-800' : c.contract_state === 'NSW' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{c.contract_state === 'QLD_SA' ? 'QLD/SA' : c.contract_state}</Badge>
                            {c.status === 'sent' ? <Badge className="bg-green-100 text-green-800">Sent</Badge> : <Badge variant="outline">Draft</Badge>}
                            {outcomeBadge}
                          </div>
                          <p className="text-xs text-slate-500">
                            {c.contact_name && <span>{c.contact_name} | </span>}
                            {c.accounts_email && <span>{c.accounts_email} | </span>}
                            {c.commission_type && c.commission_value && <span className="text-emerald-600 font-medium">Comm: {c.commission_value}{c.commission_type === 'percent' ? '%' : ' c/kWh'} | </span>}
                            Created {new Date(c.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {/* Outcome buttons */}
                          <div className="flex gap-0.5 mr-1 border-r pr-2">
                            <button onClick={function() { updateOutcome(c.id, 'won'); }} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'won' ? 'bg-green-100 text-green-700' : 'text-slate-300 hover:text-green-600 hover:bg-green-50')} title="Won" data-testid={'outcome-won-' + c.id}><Trophy className="w-4 h-4" /></button>
                            <button onClick={function() { updateOutcome(c.id, 'pending'); }} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'text-slate-300 hover:text-yellow-600 hover:bg-yellow-50')} title="Pending" data-testid={'outcome-pending-' + c.id}><Clock3 className="w-4 h-4" /></button>
                            <button onClick={function() { updateOutcome(c.id, 'lost'); }} className={'p-1.5 rounded transition-colors ' + (c.outcome === 'lost' ? 'bg-red-100 text-red-700' : 'text-slate-300 hover:text-red-600 hover:bg-red-50')} title="Lost" data-testid={'outcome-lost-' + c.id}><XCircle className="w-4 h-4" /></button>
                          </div>
                          <Button variant="ghost" size="sm" onClick={function() { loadContract(c); }} className="text-blue-600" data-testid={'edit-' + c.id}><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={function() {
                            setGenerating(true);
                            axios.get(API_URL + '/api/contracts/' + c.id + '/pdf?authorization=' + token, { responseType: 'blob' })
                              .then(function(r) { var url = window.URL.createObjectURL(new Blob([r.data])); var a = document.createElement('a'); a.href = url; a.download = 'NBE_Contract_' + (c.agreement_number || c.id) + '.pdf'; a.click(); })
                              .catch(function() { toast.error('PDF failed'); })
                              .finally(function() { setGenerating(false); });
                          }} className="text-[#005394]"><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={function() { setEmailContractId(c.id); setEmailTo(c.accounts_email || ''); setEmailDialog(true); }} className="text-green-600"><Send className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={function() { handleMailto(c.id, c.company_name, c.accounts_email, c); }} className="text-amber-600" data-testid={'mailto-' + c.id} title="Download PDF & open in mail client"><Mail className="w-4 h-4" /></Button>
                          {user && user.role === 'admin' && <Button variant="ghost" size="sm" onClick={function() { deleteContract(c.id); }} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* ====== RATE CARDS ====== */}
          <TabsContent value="ratecards">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: Upload + List */}
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2"><Upload className="w-5 h-5" />Upload Rate Card</CardTitle>
                    <CardDescription>Upload an NBE C&I Rate Card Excel file</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 hover:border-[#005394] transition-colors" data-testid="rate-card-upload-area">
                      {rateCardUploading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin" />Uploading & parsing...</div>
                      ) : (
                        <div className="flex flex-col items-center text-sm text-slate-500">
                          <Upload className="w-6 h-6 mb-1 text-[#005394]" />
                          <span className="font-medium">Click to upload .xlsx</span>
                        </div>
                      )}
                      <input type="file" accept=".xlsx,.xls" onChange={handleRateCardUpload} className="hidden" disabled={rateCardUploading} data-testid="rate-card-file-input" />
                    </label>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" />Upload History</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {rateCards.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No rate cards uploaded yet</p>}
                    {rateCards.map(function(rc) {
                      var isSelected = selectedRateCard === rc.id;
                      return (
                        <div key={rc.id} onClick={function() { viewRateCard(rc.id); }} className={'flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ' + (isSelected ? 'bg-[#005394]/5 border-[#005394] shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50')} data-testid={'rate-card-' + rc.id}>
                          <div className="min-w-0 flex-1">
                            <p className={'text-sm font-medium truncate ' + (isSelected ? 'text-[#005394]' : 'text-slate-700')}>{rc.filename}</p>
                            <p className="text-xs text-slate-400">{new Date(rc.uploaded_at).toLocaleString()} - {rc.uploaded_by_name}</p>
                          </div>
                          <div className="flex gap-1 ml-2 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={function(e) { e.stopPropagation(); var a = document.createElement('a'); a.href = API_URL + '/api/ci-rate-cards/' + rc.id + '/download?authorization=' + token; a.download = (rc.name || 'rate_card') + '.xlsx'; document.body.appendChild(a); a.click(); document.body.removeChild(a); }} data-testid={'download-rc-' + rc.id}>
                              <Download className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            {user && user.role === 'admin' && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={function(e) { e.stopPropagation(); deleteRateCard(rc.id); }} data-testid={'delete-rc-' + rc.id}>
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>

              {/* Right: Rate Card Detail + Apply */}
              <div className="lg:col-span-2">
                {!rateCardDetail ? (
                  <Card><CardContent className="py-16 text-center"><Database className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Select or upload a rate card to view its contents</p></CardContent></Card>
                ) : rateCardLoading ? (
                  <Card><CardContent className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#005394] mx-auto" /></CardContent></Card>
                ) : (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{rateCardDetail.filename}</CardTitle>
                          <CardDescription>Uploaded {new Date(rateCardDetail.uploaded_at).toLocaleString()} by {rateCardDetail.uploaded_by_name}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <RateCardDataView data={rateCardDetail.data} contractState={form.contract_state} />
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ====== HTML TEMPLATE EDITOR ====== */}
          {user && user.role === 'admin' && (
            <TabsContent value="template">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Code className="w-5 h-5" />Contract HTML Template</CardTitle>
                  <CardDescription>Edit the HTML template used for contract PDF generation. Use placeholders like {'{{company_name}}'}, {'{{abn}}'}, etc.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={templateEnabled} onChange={function(e) { setTemplateEnabled(e.target.checked); }} className="rounded" />
                      Enable custom template (overrides default)
                    </label>
                    <Button onClick={saveTemplate} disabled={templateSaving} size="sm" data-testid="save-template-btn">
                      {templateSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save Template
                    </Button>
                    <Button variant="outline" onClick={loadDefaultTemplate} size="sm" data-testid="load-default-template-btn">
                      <FileText className="w-4 h-4 mr-1" />Load Default Template
                    </Button>
                    <Button variant="outline" onClick={handlePreviewTemplate} disabled={previewLoading} size="sm" className="border-[#00A99D] text-[#00A99D] hover:bg-[#00A99D]/10" data-testid="preview-template-btn">
                      {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}Preview PDF
                    </Button>
                  </div>
                  <textarea
                    value={templateHtml}
                    onChange={function(e) { setTemplateHtml(e.target.value); }}
                    className="w-full h-[500px] font-mono text-xs border rounded-lg p-3 bg-slate-900 text-green-400 focus:outline-none focus:ring-2 focus:ring-[#005394]"
                    placeholder="Paste your custom HTML template here..."
                    data-testid="template-editor"
                  />
                  <div className="mt-3">
                    <p className="text-xs text-slate-500 font-semibold mb-1">Available Placeholders:</p>
                    <div className="text-xs text-slate-400 leading-relaxed space-y-2">
                      <div><span className="font-medium text-slate-500">Customer:</span> {'{{company_name}} {{trading_name}} {{customer_type}} {{abn}} {{acn}} {{contact_name}} {{phone}} {{mobile}} {{accounts_email}} {{billing_address}} {{billing_suburb}} {{billing_state}} {{billing_postcode}} {{id_number}} {{id_type}} {{id_expiry}} {{dob}}'}</div>
                      <div><span className="font-medium text-slate-500">Connection:</span> {'{{connection_type}} {{contract_state}} {{contract_term_months}} {{proposed_transfer_date}} {{next_meter_read_date}} {{quote_valid_until}} {{agreement_number}} {{representative_name}}'}</div>
                      <div><span className="font-medium text-slate-500">Supply (Site 1):</span> {'{{supply_address}} {{supply_suburb}} {{supply_state}} {{supply_postcode}} {{supply_nmi}} {{supply_estimated_pk}} {{supply_estimated_opk}} {{supply_estimated_kwh}} {{supply_tariff}}'}</div>
                      <div><span className="font-medium text-slate-500">Supply (Multi-site):</span> {'{{supply_1_address}} {{supply_1_nmi}} ... {{supply_2_address}} {{supply_2_nmi}} etc.'}</div>
                      <div><span className="font-medium text-slate-500">Pricing - Stepped:</span> {'{{offer_type}} {{step_y1_start}} {{step_y1_peak}} {{step_y1_offpeak}} {{step_y1_shoulder}} {{step_y2_start}} {{step_y2_peak}} {{step_y2_offpeak}} {{step_y2_shoulder}} {{step_y3_start}} {{step_y3_peak}} {{step_y3_offpeak}} {{step_y3_shoulder}}'}</div>
                      <div><span className="font-medium text-slate-500">Pricing - Smoothed:</span> {'{{smooth_24_start}} {{smooth_24_peak}} {{smooth_24_offpeak}} {{smooth_24_shoulder}} {{smooth_36_start}} {{smooth_36_peak}} {{smooth_36_offpeak}} {{smooth_36_shoulder}}'}</div>
                      <div><span className="font-medium text-slate-500">Environmental:</span> {'{{lgcs_cal26}} {{lgcs_cal27}} {{lgcs_cal28}} {{lgcs_cal29}} {{stcs_cal26}} {{stcs_cal27}} {{stcs_cal28}} {{stcs_cal29}} {{state_scheme_cal26}} {{state_scheme_cal27}} {{state_scheme_cal28}} {{state_scheme_cal29}} {{prr_cal26}} {{prr_cal27}} {{prr_cal28}} {{prr_cal29}} {{env_kwh_cal26}} {{env_kwh_cal27}} {{env_kwh_cal28}} {{env_kwh_cal29}}'}</div>
                      <div><span className="font-medium text-slate-500">Other Charges:</span> {'{{service_fee_monthly}} {{dma_admin_fee_daily}} {{metering_charge_monthly}} {{load_flex_percent}}'}</div>
                      <div><span className="font-medium text-slate-500">Signatures:</span> {'{{customer_sign_name}} {{customer_sign_position}} {{customer_sign_date}} {{nbe_sign_name}} {{nbe_sign_position}} {{nbe_sign_date}}'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Email Dialog */}
        <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Send Contract to Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Customer Email *" value={emailTo} onChange={function(e) { setEmailTo(e.target.value); }} placeholder="customer@example.com" />
              <Field label="Subject (optional)" value={emailSubject} onChange={function(e) { setEmailSubject(e.target.value); }} placeholder="Auto-generated if empty" />
              <div>
                <Label className="text-xs text-slate-600">Message (optional)</Label>
                <textarea value={emailMessage} onChange={function(e) { setEmailMessage(e.target.value); }} className="w-full h-24 border rounded-md p-2 text-sm" placeholder="Custom message (auto-generated if empty)" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={function() { setEmailDialog(false); }} className="flex-1">Cancel</Button>
                <Button onClick={handleSendEmail} disabled={sendingEmail} className="flex-1 bg-[#005394]" data-testid="confirm-send-email">
                  {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <AlertDialog open={!!confirmDelete} onOpenChange={function(open) { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && confirmDelete.type === 'contract' ? 'Are you sure you want to delete this contract? This action cannot be undone.' : 'Are you sure you want to delete this rate card? This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAction} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function CollapsibleSection({ title, sKey, expanded, toggle, children }) {
  return (
    <div className="mb-1">
      <button type="button" onClick={function() { toggle(sKey); }} className="w-full flex items-center justify-between py-2 border-b-2 border-[#29AAE0] mb-3 mt-3 group" data-testid={'section-' + sKey}>
        <h3 className="text-sm font-bold text-[#005394] uppercase tracking-wide">{title}</h3>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#005394]" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {expanded && <div className="pb-1">{children}</div>}
    </div>
  );
}

function RateCardDataView({ data, contractState }) {
  if (!data) return null;
  var stateKey = contractState === 'QLD_SA' ? 'QLD' : contractState;
  var altStateKey = contractState === 'QLD_SA' ? 'SA' : null;

  function fmtVal(v) {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'number') return v.toFixed(2);
    return String(v);
  }

  function RateTable({ quarter, label }) {
    var q = data.quarters && data.quarters[quarter];
    if (!q || !q.energy_rates) return null;
    var states = contractState === 'QLD_SA' ? ['QLD', 'SA'] : [stateKey];
    return (
      <div className="mb-4">
        <h4 className="text-sm font-bold text-[#005394] mb-2">{label} - Energy Rates (c/kWh ex GST)</h4>
        {states.map(function(st) {
          var sr = q.energy_rates[st];
          if (!sr) return <p key={st} className="text-xs text-slate-400 mb-2">No data for {st}</p>;
          return (
            <div key={st} className="mb-3">
              <p className="text-xs font-semibold text-slate-600 mb-1">{st}</p>
              <table className="w-full text-xs border-collapse border rounded" data-testid={'rate-table-' + quarter + '-' + st}>
                <thead><tr className="bg-[#005394] text-white"><th className="p-1.5 text-left">Offer</th><th className="p-1.5 text-center">Term</th><th className="p-1.5 text-center">Peak</th><th className="p-1.5 text-center">Shoulder</th><th className="p-1.5 text-center">Off Peak</th><th className="p-1.5 text-center">Svc $/mo</th></tr></thead>
                <tbody>
                  {['stepped', 'smoothed'].map(function(offer) {
                    var od = sr[offer];
                    if (!od) return null;
                    return Object.keys(od).sort().map(function(term, i) {
                      var r = od[term];
                      return (
                        <tr key={offer + term} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                          {i === 0 && <td className="p-1.5 font-medium capitalize border-r" rowSpan={Object.keys(od).length}>{offer}</td>}
                          <td className="p-1.5 text-center">{term}yr</td>
                          <td className="p-1.5 text-center font-mono">{fmtVal(r.peak)}</td>
                          <td className="p-1.5 text-center font-mono">{fmtVal(r.shoulder)}</td>
                          <td className="p-1.5 text-center font-mono">{fmtVal(r.offpeak)}</td>
                          <td className="p-1.5 text-center font-mono">{fmtVal(r.service_charge)}</td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="rate-card-detail-view">
      <RateTable quarter="Q1" label="Q1 (Current Quarter)" />
      <RateTable quarter="Q2" label="Q2 (Next Quarter)" />

      {/* Environmental */}
      {data.environmental && Object.keys(data.environmental).length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-[#005394] mb-2">Environmental - Certificate Prices ($/MWh ex GST)</h4>
          <table className="w-full text-xs border-collapse border rounded" data-testid="env-rate-table">
            <thead><tr className="bg-[#005394] text-white"><th className="p-1.5 text-left">Scheme</th><th className="p-1.5 text-center">CAL25</th><th className="p-1.5 text-center">CAL26</th><th className="p-1.5 text-center">CAL27</th><th className="p-1.5 text-center">CAL28</th></tr></thead>
            <tbody>
              {Object.entries(data.environmental).map(function(entry, i) {
                return (
                  <tr key={entry[0]} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="p-1.5 font-medium">{entry[0]}</td>
                    <td className="p-1.5 text-center font-mono">{fmtVal(entry[1].CAL25)}</td>
                    <td className="p-1.5 text-center font-mono">{fmtVal(entry[1].CAL26)}</td>
                    <td className="p-1.5 text-center font-mono">{fmtVal(entry[1].CAL27)}</td>
                    <td className="p-1.5 text-center font-mono">{fmtVal(entry[1].CAL28)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Metering */}
      {data.metering && Object.keys(data.metering).length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-[#005394] mb-2">Metering Charges (ex GST per month)</h4>
          <table className="w-full text-xs border-collapse border rounded" data-testid="metering-rate-table">
            <thead><tr className="bg-[#005394] text-white"><th className="p-1.5 text-left">Type</th><th className="p-1.5 text-center">Amount ($)</th></tr></thead>
            <tbody>
              {Object.entries(data.metering).map(function(entry, i) {
                return (
                  <tr key={entry[0]} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="p-1.5 font-medium">{entry[0]}</td>
                    <td className="p-1.5 text-center font-mono">{fmtVal(entry[1])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
